import { TriangleAlert } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { type AiSuggestionView, type AiStopReason } from '@apm/contracts';
import { skillAvailability, useAiCapabilities } from '../use-ai-capabilities';

/**
 * Dải vàng cho gợi ý **một phần** — APM-H-p.
 *
 * Run chạm trần khi trợ lý còn muốn đọc thêm; harness cho một lượt kết và ghi
 * kết quả với `partial: true`. Người duyệt phải biết điều đó **trước** khi đọc
 * nội dung: gợi ý này dựng từ dữ liệu chưa đủ, và phần thiếu có đường làm tay.
 * Không phải lỗi, không phải kết quả đầy đủ — một loại thứ ba, hiện khác hai loại kia.
 */
export function PartialSuggestionNotice({
  suggestion,
  className,
}: {
  suggestion: Pick<AiSuggestionView, 'skill' | 'partial' | 'stopReason' | 'unverified'>;
  className?: string;
}) {
  const { t } = useTranslation();
  const { data: capabilities } = useAiCapabilities();
  const unverified = suggestion.unverified ?? [];
  if (!suggestion.partial && unverified.length === 0) return null;

  const manual = skillAvailability(suggestion.skill, capabilities).manual;
  // Chỉ "chưa kiểm được" (APM-H-n): không phải một phần, không phải lỗi — cửa
  // cuối không có dữ liệu để nói đúng hay sai, và người duyệt phải biết điều đó.
  if (!suggestion.partial) {
    return (
      <div
        role="status"
        className={`flex items-start gap-2 rounded-DEFAULT border border-amber-300 bg-amber-50 p-2 text-xs text-amber-900 dark:border-amber-700 dark:bg-amber-950/40 dark:text-amber-100 ${className ?? ''}`}
      >
        <TriangleAlert className="mt-0.5 size-4 shrink-0" aria-hidden />
        <div className="flex flex-col gap-1">
          <span>{t('ai.activity.unverified', { count: unverified.length })}</span>
          <ul className="list-disc pl-4 text-amber-800 dark:text-amber-200">
            {unverified.map((item) => (
              <li key={item.path}>{item.reason}</li>
            ))}
          </ul>
        </div>
      </div>
    );
  }
  return (
    <div
      role="status"
      className={`flex items-start gap-2 rounded-DEFAULT border border-amber-300 bg-amber-50 p-2 text-xs text-amber-900 dark:border-amber-700 dark:bg-amber-950/40 dark:text-amber-100 ${className ?? ''}`}
    >
      <TriangleAlert className="mt-0.5 size-4 shrink-0" aria-hidden />
      <div className="flex flex-col gap-1">
        <span>
          {t('ai.activity.partial', { reason: stopReasonLabel(suggestion.stopReason, t) })}
        </span>
        {manual ? (
          <span className="text-amber-800 dark:text-amber-200">
            {t('ai.activity.partialManual', { label: manual.label })}
          </span>
        ) : null}
        {unverified.length > 0 ? (
          <ul className="list-disc pl-4 text-amber-800 dark:text-amber-200">
            {unverified.map((item) => (
              <li key={item.path}>{item.reason}</li>
            ))}
          </ul>
        ) : null}
      </div>
    </div>
  );
}

export function stopReasonLabel(
  reason: AiStopReason | null | undefined,
  t: (key: string) => string,
): string {
  return reason ? t(`ai.activity.stopReason.${reason}`) : t('ai.activity.stopReason.turn_capped');
}
